import { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Paperclip,
  Upload,
  Download,
  Trash2,
  File,
  FileText,
  Image,
  FileSpreadsheet,
  AlertCircle,
} from 'lucide-react';
import {
  CardDescription,
  CardTitle,
} from '@/components/ui/card';
import type { TaskAttachment } from '@/types/task';

interface TaskDetailAttachmentsProps {
  readonly taskId: string;
  readonly attachments: TaskAttachment[];
  readonly onAttachmentAdded: (attachment: TaskAttachment) => void;
  isEditMode?: boolean;
}

export function TaskDetailAttachments({
  taskId,
  attachments,
  onAttachmentAdded,
  isEditMode = false,
}: Readonly<TaskDetailAttachmentsProps>) {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files?.[0]) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFiles(e.target.files);
    }
  };

  const handleFiles = async (files: FileList) => {
    setUploading(true);
    setUploadProgress(0);

    // Simulate upload
    for (let i = 0; i <= 100; i += 10) {
      await new Promise(resolve => setTimeout(resolve, 100));
      setUploadProgress(i);
    }

    // Create attachment record
    const file = files[0];
    const attachment: TaskAttachment = {
      id: `attach-${Date.now()}`,
      taskId,
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type,
      uploadedAt: new Date().toISOString(),
      uploadedBy: 'user-1',
      uploadedByName: 'Current User',
      url: '#',
    };

    onAttachmentAdded(attachment);
    setUploading(false);
    setUploadProgress(0);
  };

  const handleDelete = (attachmentId: string) => {
    if (confirm('Are you sure you want to delete this attachment?')) {
      // TODO: Implement delete
      console.log('Delete attachment:', attachmentId);
    }
  };

  const getFileIcon = (fileType: string) => {
    if (fileType.startsWith('image/')) {
      return <Image className="h-5 w-5" />;
    } else if (fileType.includes('pdf')) {
      return <FileText className="h-5 w-5" />;
    } else if (fileType.includes('spreadsheet') || fileType.includes('excel')) {
      return <FileSpreadsheet className="h-5 w-5" />;
    } else {
      return <File className="h-5 w-5" />;
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <div className={`grid grid-cols-1 gap-6 ${isEditMode ? 'lg:grid-cols-3' : ''}`}>
      {/* Left Column - Attachments List (2/3 width) */}
      <div className={isEditMode ? 'lg:col-span-2' : ''}>
        <Card className="border border-border/40">
          <div className="p-6">
            <CardTitle className="flex items-center gap-2 mb-2">
              <Paperclip className="h-5 w-5" />
              Attachments
            </CardTitle>
            <CardDescription className="text-xs mb-4">
              Files and documents related to this task
            </CardDescription>
            {attachments.length === 0 ? (
              <div className="text-center py-12">
                <Paperclip className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">No attachments</h3>
                <p className="text-muted-foreground">
                  Upload files using the form on the right
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {attachments.map((attachment) => (
                  <Card key={attachment.id} className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="text-muted-foreground">
                        {getFileIcon(attachment.fileType)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">{attachment.fileName}</div>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <span>{formatFileSize(attachment.fileSize)}</span>
                          <span>•</span>
                          <span>
                            Uploaded by {attachment.uploadedByName} on{' '}
                            {new Date(attachment.uploadedAt).toLocaleDateString()}
                          </span>
                        </div>
                        {attachment.virusScanStatus === 'scanning' && (
                          <Badge variant="outline" className="mt-1">
                            <AlertCircle className="h-3 w-3 mr-1" />
                            Scanning...
                          </Badge>
                        )}
                        {attachment.virusScanStatus === 'failed' && (
                          <Badge variant="destructive" className="mt-1">
                            <AlertCircle className="h-3 w-3 mr-1" />
                            Virus detected
                          </Badge>
                        )}
                      </div>
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" asChild>
                          <a href={attachment.url} download>
                            <Download className="h-4 w-4" />
                          </a>
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDelete(attachment.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* Right Column - Upload Area (1/3 width) */}
      {isEditMode && (
      <div className="lg:col-span-1">
        <Card className="sticky top-4 border border-border/40">
          <div className="p-6">
            <CardTitle className="flex items-center gap-2 mb-2 text-base">
              <Upload className="h-4 w-4" />
              Upload Files
            </CardTitle>
            <CardDescription className="text-xs mb-4">
              Add files to this task
            </CardDescription>
            
            <div
              className={`border-2 border-dashed rounded-lg p-8 transition-colors ${
                dragActive ? 'border-primary bg-primary/5' : 'border-muted-foreground/25'
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              <div className="text-center space-y-3">
                <div className="flex justify-center">
                  <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <Upload className="h-6 w-6 text-primary" />
                  </div>
                </div>
                <div>
                  <p className="text-sm font-medium mb-1">
                    Drag and drop files here
                  </p>
                  <p className="text-xs text-muted-foreground">
                    or click the button below
                  </p>
                </div>
              </div>
            </div>

            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="w-full mt-4"
            >
              <Paperclip className="h-4 w-4 mr-2" />
              Choose Files
            </Button>
            
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={handleFileInput}
              className="hidden"
            />

            {uploading && (
              <div className="mt-4 space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="font-medium">Uploading...</span>
                  <span className="text-muted-foreground">{uploadProgress}%</span>
                </div>
                <Progress value={uploadProgress} />
              </div>
            )}

            <div className="mt-4 pt-4 border-t">
              <p className="text-xs text-muted-foreground">
                Supported formats: PDF, images, documents, spreadsheets
              </p>
            </div>
          </div>
        </Card>
      </div>
      )}
    </div>
  );
}
